import pluralize from 'pluralize';

import {
  Card,
  NoData,
  Skeleton,
  InfoTooltip,
  Typography,
  Stack,
} from '@cast/design-system';
import { WorkloadReliabilitySummariesResponseItem } from '@cast/types';
import { formatPercentage, toNumOrZero } from '@cast/utils';

import { WorkloadSummaryListRows } from './WorkloadSummaryListRows';

const MAX_DISPLAYED_ITEMS = 5;

type UnhealthyWorkloadsCardProps = {
  items: WorkloadReliabilitySummariesResponseItem[];
  totalCount: string;
  moreCount: number;
  isLoading: boolean;
};

const getMoreUnhealthyLabel = (count: number) =>
  `and ${count} more unhealthy ${pluralize('workload', count)}`;

export const UnhealthyWorkloadsCard = ({
  items,
  totalCount,
  moreCount,
  isLoading,
}: UnhealthyWorkloadsCardProps) => {
  const noData = toNumOrZero(totalCount) === 0 || items.length === 0;

  return (
    <Card testId="unhealthy-workloads-card" sx={{ p: 12, mb: 12 }}>
      <Stack gap={8}>
        <Stack direction="row" alignItems="center" gap={4}>
          <Typography variant="L12R">Unhealthy workloads</Typography>
          <InfoTooltip
            title="A workload is considered unhealthy when its error rate exceeds 5%. Top unhealthy workloads are shown when the full list is not available."
            placement="top"
            iconSize="14px"
          />
        </Stack>

        {isLoading ? (
          <Skeleton height={120} />
        ) : noData ? (
          <NoData
            testId="unhealthy-workloads-healthy"
            title="No unhealthy workloads"
            hexProps={{ size: 40 }}
            stackProps={{ py: 8 }}
          />
        ) : (
          <Stack gap={8}>
            <WorkloadSummaryListRows
              items={items.slice(0, MAX_DISPLAYED_ITEMS)}
              formatMetric={(metric) => formatPercentage(metric)}
              rowTestId="unhealthy-workloads-row"
              variant="compact"
            />
            {moreCount > 0 && (
              <Typography
                variant="P12R"
                color="textSecondary"
                data-testid="unhealthy-workloads-more"
              >
                {getMoreUnhealthyLabel(moreCount)}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
